import React, { useContext } from 'react';
import styled from 'styled-components';

import Icon from '../../misc/Icon';
import Button from '../../misc/Button';
import InputSettingsField from './InputSettingsField';
import { getFieldInputType, getDisplayValue, getChangeValue } from '../fields/TextField';

import ThemeIcons from '../../../theme/Icons';
import ThemeContext from '../../../theme/ThemeContext';

import ConditionalLogic from '../../../constants/ConditionalLogic';
import FieldTypes from '../../../constants/FieldTypes';

import { sortFieldPositionsByXAndYCoordinates } from '../../../lib/documentHelper';

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledStepLabel = styled.div`
  font-weight: 500;
  margin-left: 6px;
  font-size: 14px;
  color: ${(props) => props.theme.fontColorPrimary};
`;

const StyledConditionColumn = styled.div`
  display: flex;
  flex-direction: column;
  ${(props) => (props.conditions && props.conditions.length > 1 ? 'max-height: 450px; overflow-y: auto;' : '')};
`;

const StyledConditionRow = styled.div`
  display: flex;
  flex: 1;
  margin-bottom: 8px;
  flex-direction: row;
  align-items: center;
`;

export const LogicSettingsForm = ({
  fileId,
  targetLabel,
  fields,
  sortedPages,
  targetPageId,
  targetFieldId,
  logic,
  onChange
}) => {

  const theme = useContext(ThemeContext);

  const formattedSortedPages = sortedPages
    //Step 1: Add easily identifable page label for selector
    .map((page, i) => ({ ...page, label: `Page ${i + 1}` }))
    //Step 2: Remove currently active page. We don't allow selection of conditions from own page.
    .filter((page) => page._id !== targetPageId);

  const fieldLookup = {};
  fields.forEach((field) => fieldLookup[field._id] = field);

  const handleChange = (key, value) => {

    const nextLogic = JSON.parse(JSON.stringify(logic));

    nextLogic[key] = value;

    if (onChange) onChange(nextLogic);

  };

  const handleConditionChange = (index, changes) => {

    const nextLogic = JSON.parse(JSON.stringify(logic));

    if (!nextLogic.conditions[index]) return;

    nextLogic.conditions[index] = {
      ...nextLogic.conditions[index],
      ...changes
    };

    onChange(nextLogic);

  };

  const handleConditionCreate = () => {

    const nextLogic = JSON.parse(JSON.stringify(logic));

    if (nextLogic.conditions) {
      nextLogic.conditions.push({ file: fileId });
    } else {
      nextLogic.conditions = [{ file: fileId }];
    }

    onChange(nextLogic);

  };

  const handleConditionDelete = (index) => {

    const nextLogic = JSON.parse(JSON.stringify(logic));

    nextLogic.conditions.splice(index, 1);

    onChange(nextLogic);

  };

  return (
    <StyledWrapper>
      <StyledConditionRow>
        <div style={{ width: '75px' }}>
          <InputSettingsField
            type="dropdown"
            value={logic.action}
            options={[
              { _id: 'show', value: 'Show' },
              { _id: 'hide', value: 'Hide' },
            ]}
            onChange={(i, value) => handleChange('action', value)}
          />
        </div>
        <StyledStepLabel theme={theme}>
          this
          {' '}
          {targetLabel}
          {' '}
          when
        </StyledStepLabel>
      </StyledConditionRow>
      <StyledConditionRow>
        <div style={{ width: '75px' }}>
          <InputSettingsField
            type="dropdown"
            inputStyles={{ maxWidth: '100px' }}
            value={logic.eval}
            options={[
              { _id: 'and', value: 'All' },
              { _id: 'or', value: 'Any' },
            ]}
            onChange={(i, value) => handleChange('eval', value)}
          />
        </div>
        <StyledStepLabel theme={theme}>of the following conditions match</StyledStepLabel>
      </StyledConditionRow>
      <StyledConditionRow style={{ marginBottom: 0 }}>
        <StyledConditionColumn style={{ flex: 1 }} conditions={logic?.conditions}>
          {logic?.conditions?.map((condition, index) => (
            <LogicCondition
              key={index}
              index={index}
              fieldLookup={fieldLookup}
              pages={formattedSortedPages}
              targetFieldId={targetFieldId}
              {...condition}
              onChange={handleConditionChange}
              onDelete={handleConditionDelete}
            />
          ))}
        </StyledConditionColumn>
      </StyledConditionRow>
      <StyledConditionRow>
        <Button
          icon={ThemeIcons.misc.add}
          onClick={handleConditionCreate}
        />
      </StyledConditionRow>
    </StyledWrapper>
  );

};

const StyledConditionWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  padding: 8px;
  margin-bottom: 8px;
  padding-bottom: 2px;
  border-radius: ${(props) => props.theme.borderRadius}px;
  border: 1px solid ${(props) => props.theme.borderColor};
  background-color: ${(props) => props.theme.secondaryColor};
`;

const StyledValueNodeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-left: 4px;
`;

const StyledConditionSelectWrapper = styled.div`
  display: flex;
`;

const StyledConditionDelete = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
`;

export const LogicCondition = ({

  //Lookup options
  fieldLookup, //All fields from doc.fields
  pages, //Pages sorted by pageOrder
  targetFieldId,

  //Condition Properties
  index,
  file,
  page,
  field,
  condition,
  value,
  onChange,
  onDelete,

}) => {

  const theme = useContext(ThemeContext);

  const handleChange = (key, val) => {

    const nextForm = { file, page, field, condition, value, [key]: val };

    if (key === 'page') {
      delete nextForm.field;
      delete nextForm.value;
    } else if (key === 'field') {
      delete nextForm.value;
    }

    if (onChange) onChange(index, nextForm);

  };

  const selectedPage = !page ? null : pages.find((p) => p._id === page);

  const selectedPageFieldsLookup = {};
  const selectedPageFields = !selectedPage ? null : [];

  if (selectedPage) {

    const sortedFieldPositions = sortFieldPositionsByXAndYCoordinates(selectedPage.fieldPositions);

    sortedFieldPositions.forEach((fieldPosition) => {

      /**
       * 1. Remove currently active field. We don't allow selection of conditions from own field.
       * 2. Ignore any unsupported field types
       */
      if (fieldPosition.field === targetFieldId) return;

      if (
        fieldPosition.type !== FieldTypes.text
        && fieldPosition.type !== FieldTypes.textarea
        && fieldPosition.type !== FieldTypes.number
        && fieldPosition.type !== FieldTypes.dropdown
        && fieldPosition.type !== FieldTypes.multiSelect
      ) return;

      /**
       * Only add fields to the page field list if they haven't already been added.
       * We do this because multiple page fieldPositions could link to the same field.
       */
      if (!selectedPageFieldsLookup[fieldPosition.field]) {
        selectedPageFields.push(fieldLookup[fieldPosition.field]);
        selectedPageFieldsLookup[fieldPosition.field] = true;
      }

    });
  }

  const selectedField = !field ? null : fieldLookup[field];

  const pageOptions = pages?.map((page) => {
    return { _id: page._id, value: page.label };
  });

  const fieldOptions = selectedPageFields?.map((field) => {
    return { _id: field._id, value: field.title || field.identifier || field.type };
  });

  let conditionOptions = [];
  let valueNode = null;

  if (
    selectedField &&
    (selectedField.type === FieldTypes.multiSelect || selectedField.type === FieldTypes.dropdown)
  ) {

    conditionOptions = [
      { _id: ConditionalLogic.conditions.filled, value: 'is filled' },
      { _id: ConditionalLogic.conditions.empty, value: 'is empty' },
      { _id: ConditionalLogic.conditions.equal, value: 'is' },
      { _id: ConditionalLogic.conditions.notEqual, value: 'is not' },
    ];

    const fieldOptions = [];
    if (selectedField && selectedField.options) {
      selectedField.options.forEach((opt) => {
        if (!opt.deleted) fieldOptions.push({ _id: opt._id, value: opt.value });
      });
    }

    valueNode = (
      <InputSettingsField
        value={value}
        type="dropdown"
        options={fieldOptions}
        onChange={(i, value) => handleChange('value', value)}
      />
    );

  } else if (
    selectedField
    && (selectedField.type === FieldTypes.number)
  ) {

    conditionOptions = [
      { _id: ConditionalLogic.conditions.filled, value: 'is filled' },
      { _id: ConditionalLogic.conditions.empty, value: 'is empty' },
      { _id: ConditionalLogic.conditions.equal, value: 'is' },
      { _id: ConditionalLogic.conditions.notEqual, value: 'is not' },
      { _id: ConditionalLogic.conditions.greaterThan, value: 'is greater than' },
      { _id: ConditionalLogic.conditions.lessThan, value: 'is less than' },

    ];

    valueNode = (
      <InputSettingsField
        type={getFieldInputType(false, selectedField.type, selectedField.format)}
        value={getDisplayValue(selectedField.type, value, selectedField.format)}
        onChange={(i, value) => handleChange('value', getChangeValue(selectedField.type, value, selectedField.format))}
      />
    );

  } else if (
    selectedField
    && (selectedField.type === FieldTypes.text || selectedField.type === FieldTypes.textarea)
  ) {

    conditionOptions = [
      { _id: ConditionalLogic.conditions.filled, value: 'is filled' },
      { _id: ConditionalLogic.conditions.empty, value: 'is empty' },
      { _id: ConditionalLogic.conditions.equal, value: 'is' },
      { _id: ConditionalLogic.conditions.notEqual, value: 'is not' },
      { _id: ConditionalLogic.conditions.contain, value: 'contains' },
    ];

    valueNode = (
      <InputSettingsField
        type={getFieldInputType(false, selectedField.type, selectedField.format)}
        value={getDisplayValue(selectedField.type, value, selectedField.format)}
        onChange={(i, value) => handleChange('value', getChangeValue(selectedField.type, value, selectedField.format))}
      />
    );

  }

  return (
    <StyledConditionWrapper theme={theme}>
      <StyledConditionColumn style={{ flex: 1 }}>
        <StyledConditionRow>
          <InputSettingsField
            label="Page"
            value={page}
            type="dropdown"
            options={pageOptions}
            onChange={(i, pageId) => handleChange('page', pageId)}
          />
        </StyledConditionRow>
        {!selectedPage ? null : (
          <StyledConditionRow>
            <InputSettingsField
              label="Field"
              value={field}
              type="dropdown"
              options={fieldOptions}
              onChange={(i, value) => handleChange('field', value)}
            />
          </StyledConditionRow>
        )}
        {!selectedPage || !selectedField ? null : (
          <StyledConditionRow>
            <StyledConditionSelectWrapper>
              <InputSettingsField
                value={condition}
                type="dropdown"
                options={conditionOptions}
                onChange={(i, value) => handleChange('condition', value)}
              />
            </StyledConditionSelectWrapper>
            <StyledValueNodeWrapper>
              {condition === ConditionalLogic.conditions.empty || condition === ConditionalLogic.conditions.filled ? null : valueNode}
            </StyledValueNodeWrapper>
          </StyledConditionRow>
        )}
      </StyledConditionColumn>
      <StyledConditionColumn>
        <StyledConditionDelete>
          <Icon
            color={theme.dangerColor}
            icon={ThemeIcons.settings.delete}
            size="sm"
            onClick={() => onDelete(index)}
          />
        </StyledConditionDelete>
      </StyledConditionColumn>
    </StyledConditionWrapper>
  );
};

export default LogicSettingsForm;
